// Imports
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Params, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled, { useTheme } from 'styled-components/macro';
import { styling, StylingProps } from '../../../../../../shareables/foundation/front-end/components/buttons/standard';
import { useAppDispatch } from '../../../../../../shareables/foundation/front-end/redux/hooks';
import apiActions from '../../../../../../shareables/integrations/api/redux/actions';
import theme from '../../../../../../shareables/theme';
import {
	GetEventsParameters,
	GetEventsResponse,
} from '../../../../../../shareables/types/api/main/website/page/type/202/events/get';
import { EventInput, EventSourceFuncArg } from '@fullcalendar/core';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import Skeleton from 'react-loading-skeleton';
import range from 'lodash/range';
import modalHelpers from '../../../../../../shareables/front-end/modal/modal-helpers';
import EventDetailsModal from './event-details-modal';
import { GeneratedThemeProps } from '../../../../../templates/template-style';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRefresh } from '@fortawesome/pro-solid-svg-icons';
import formatEventURI from './utils/format-event-title';
import { darken } from 'polished';


// Styled components
const CalendarContainer = styled.div<StylingProps & { theme: GeneratedThemeProps }>`
	.fc-button-primary.fc-button-primary,
	.fc-button-primary.fc-button-primary:disabled,
	.fc-button-primary.fc-button-primary:hover {
		${styling}
	}
	
	.fc-button-group > .fc-button:not(:last-child) {
		border-bottom-right-radius: 0;
		border-top-right-radius: 0;
	}
	
	.fc-button-group > .fc-button:not(:first-child) {
		border-bottom-left-radius: 0;
		border-top-left-radius: 0;
	}
	
	.fc-button-group > .fc-button:focus {
		z-index: 100;
	}
	
	.fc-toolbar-title {
		font-weight: 400;
		font-size: 1.4rem !important;
	}
	
	.holiday > .fc-event-main {
		color: #333333 !important;
	}
	
	.holiday,
	.event {
		font-size: 0.8em;
	}
	
	.loading {
		cursor: default;
	}
	
	.fc-daygrid-day-number,
	.fc-col-header-cell-cushion {
		cursor: default;
		pointer-events: none;
	}
	
	.fc-daygrid-day-number,
	.fc-col-header-cell-cushion {
		color: inherit;
	}
	
	.fc-header-toolbar {
		@media (max-width: ${theme.breakpoints.mobile}px) {
			flex-direction: column;
		}
	}
	
	.fc-daygrid-dot-event {
		:hover,
		:focus {
			background: unset;
		}
	}
	
	.fc-daygrid-block-event:not(.holiday) {
		:hover,
		:focus {
			background-color: ${(props) => darken(0.05, props.theme.calendarEventColor)} !important;
			border-color: ${(props) => darken(0.05, props.theme.calendarEventColor)} !important;
		}
	}
	
	.fc-daygrid-event-harness {
		overflow: hidden;
	}
	
	.fc-event-main {
		display: flex;
	}
	
	.fc-event-title {
		display: flex;
		justify-content: space-between;
		align-items: center;
		width: 100%;
		
		span {
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
			flex-grow: 1;
		}
	}
	
	.fc-day-today.fc-daygrid-day {
		background-color: transparent !important;
	}
	
	.fc-day-today .fc-daygrid-day-number {
		background-color: ${({ theme: templateStyle }) => templateStyle.calendarEventColor};
		color: #fff;
	}
	
	.fc-daygrid-day-number {
		font-size: 0.9rem;
		
		border-radius: 50%;
		width: 1.65rem;
		height: 1.65rem;
		display: flex;
		align-items: center;
		justify-content: center;
		
		margin-top: 0.25rem;
		margin-bottom: 0.25rem;
		margin-right: 0.25rem;
	}
	
	.fc-daygrid-block-event {
		padding-left: 0.125rem;
		padding-right: 0.125rem;
	}
	
	@media (max-width: ${theme.breakpoints.mobile}px) {
		.fc-daygrid-day-top {
			display: flex;
			justify-content: center;
			align-items: center;
		}
		
		.fc-daygrid-day-number {
			margin-right: 0;
		}
	}
`;

const LoadingIcon = styled(FontAwesomeIcon)`
	margin-left: auto;
`;


// Define the accepted props
interface Props {
	storybookEvents?: Record<number, GetEventsResponse>;
	pageID: number;
	pageTitle: string;
	storybookNow?: true;
}


// Function component
const Calendar: React.FC<Props> = ({ storybookEvents, pageID, pageTitle, storybookNow }) => {
	// Use state
	const [events, setEvents] = useState<Record<number, GetEventsResponse> | null>(storybookEvents ?? null);
	const [eventBeingLoaded, setEventBeingLoaded] = useState<string | null>(null);
	
	
	// Use React Router functionality
	const [searchParams, setSearchParams] = useSearchParams();
	const { eventAndOccurrenceIdentifier } = useParams<Params>();
	const navigate = useNavigate();
	
	
	// Initialize date to view from search params
	const viewMonth = searchParams.get('month');
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	
	
	// Used to detect mount status
	const isMounted = useRef(true);
	
	useEffect(() => {
		isMounted.current = true;
		
		return () => {
			isMounted.current = false;
		};
	});
	
	
	// Function that loads events by year
	const pendingYears = useMemo(() => new Set<number>(), []);
	
	const memoizedLoadEvents = useCallback(
		(year: number) => {
			if (pendingYears.has(year)) {
				return;
			}
			
			pendingYears.add(year);
			
			void dispatch(
				apiActions.call<GetEventsParameters, undefined, GetEventsResponse>({
					action: 'GET',
					uri: `/website/${window.location.hostname}/page/id_${pageID}/type/202/events`,
					parameters: {
						year,
					},
					completion: (json) => {
						pendingYears.delete(year);
						
						if (isMounted.current) {
							setEvents((events) => {
								return {
									...events,
									[year]: json.response,
								};
							});
						}
					},
					cancelOnRouteChange: false, // Required for the initial call on mount which will happen before route change detection
				})
			);
		},
		[pageID, dispatch, pendingYears]
	);
	
	
	// Helper function that loads the events for a specified year, and under some conditions the previous and next years
	const memoizedLoadEventRange = useCallback(
		(year: number) => {
			if (!events?.[year]) {
				memoizedLoadEvents(year);
			}
			
			if (year > 0 && !events?.[year - 1]) {
				memoizedLoadEvents(year - 1);
			}
			
			if (!events?.[year + 1]) {
				memoizedLoadEvents(year + 1);
			}
		},
		[memoizedLoadEvents, events]
	);
	
	
	// Initialize
	const generatedTheme = useTheme() as GeneratedThemeProps;
	
	
	// Function that builds calendar events
	const calendarEvents = useCallback(
		(info: EventSourceFuncArg) => {
			// Get range of years to build events for
			//  -> This is at most two years, the year before or after the year of the month being viewed can also be present
			const years = range(info.start.getFullYear(), info.end.getFullYear() + 1);
			
			
			// Initialize events array
			const eventsForYearsRequested: EventInput[] = [];
			
			
			// Initialize set that holds identifier for event and occurrences already handled
			const uniqueEventOccurrences = new Set<string>();
			
			
			// Build events
			for (const year of years) {
				// Assign this year's events to a variable for convenience
				const yearEvents = events?.[year];
				
				
				// Start loading this year's events
				memoizedLoadEventRange(year);
				
				
				// Handle loading events
				if (!yearEvents) {
					// Initialize mutable date
					const start = new Date(info.startStr);
					
					
					// Initialize shared attributes of loading event
					const baseLoadingEvent: EventInput = {
						title: 'Loading…',
						allDay: true,
						editable: false,
						backgroundColor: '#ebebeb',
						borderColor: '#ebebeb',
						className: 'loading',
					};
					
					
					// Handle only one year in view
					if (years.length === 1) {
						while (start.getTime() < info.end.getTime()) {
							const currentDate = new Date(start);
							
							const loadingEvent: EventInput = {
								start: currentDate,
								end: currentDate,
								...baseLoadingEvent,
							};
							
							eventsForYearsRequested.push(loadingEvent);
							
							start.setDate(start.getDate() + 1);
						}
						
						continue;
					}
					
					
					// Handle the earliest year in the range
					if (year === years[0]) {
						while (start.getFullYear() < years[years.length - 1]) {
							const currentDate = new Date(start);
							
							const loadingEvent: EventInput = {
								start: currentDate,
								end: currentDate,
								...baseLoadingEvent,
							};
							
							eventsForYearsRequested.push(loadingEvent);
							
							start.setDate(start.getDate() + 1);
						}
						
						continue;
					}
					
					
					// Handle the last year in the range
					const end = new Date(info.endStr);
					
					while (end.getFullYear() > years[0]) {
						const currentDate = new Date(end);
						
						const loadingEvent = {
							start: currentDate,
							end: currentDate,
							...baseLoadingEvent,
						};
						
						eventsForYearsRequested.push(loadingEvent);
						
						end.setDate(end.getDate() - 1);
					}
					
					continue;
				}
				
				eventsForYearsRequested.push(
					...yearEvents.holidays.map<EventInput>(({ title, start, end }) => ({
						title: title,
						start: new Date(`${start}T00:00`),
						end: new Date(`${end}T00:00`),
						allDay: true,
						editable: false,
						backgroundColor: '#ddd',
						borderColor: '#ddd',
						className: 'holiday',
						interactive: false,
					})),
					...yearEvents.events.reduce<EventInput[]>((acc, event) => {
						return acc.concat(
							event.occurrences
								.map<EventInput>(({ start, end, id }) => {
									// Build end date
									const exclusiveEnd = end ? new Date(end) : null;
									
									
									// Due to `end` being exclusive, all day events need to be set one day forward
									if (exclusiveEnd && event.allDay) {
										exclusiveEnd.setDate(exclusiveEnd.getDate() + 1);
									}
									
									
									// Return
									return {
										start: new Date(start),
										end: exclusiveEnd ?? undefined,
										title: event.title,
										allDay: event.allDay,
										backgroundColor: generatedTheme.calendarEventColor,
										borderColor: generatedTheme.calendarEventColor,
										className: 'event',
										url: `event/${formatEventURI(event.title)}-${event.id}_${id}`,
									};
								})
								// Exclude event occurrences that have already been seen
								.filter((event) => {
									if (!event.url || uniqueEventOccurrences.has(event.url)) {
										return false;
									}
									
									uniqueEventOccurrences.add(event.url);
									return true;
								})
						);
					}, [])
				);
			}
			
			
			// Return events, FullCalendar requires this to be a promise
			return new Promise<EventInput>((resolve) => resolve(eventsForYearsRequested));
		},
		[events, memoizedLoadEventRange, generatedTheme.calendarEventColor]
	);
	
	
	// Handle event detail modal
	const handleEventDetailModal = () => {
		// Do not open a modal if there's no event
		if (!eventAndOccurrenceIdentifier) {
			return;
		}
		
		
		// Open the modal
		modalHelpers.show('event-details-modal', {
			disableFocus: true,
		});
		
		
		// Remove loading indicator
		setEventBeingLoaded(null);
	};
	
	
	// Return JSX
	return (
		<CalendarContainer color='plain' border smaller>
			<FullCalendar
				height='auto'
				dayMaxEventRows={5}
				defaultTimedEventDuration='00:00:00.001'
				events={calendarEvents}
				plugins={[dayGridPlugin]}
				initialView='dayGridMonth'
				initialDate={viewMonth ? new Date(`${viewMonth}-01T00:00`) : new Date(Date.now())}
				datesSet={(calendarView) => {
					// The calendar displays part of the previous and next month, so we need to get a date between them
					const midMonth = new Date((calendarView.start.getTime() + calendarView.end.getTime()) / 2)
						.toISOString()
						.slice(0, 7);
					
					
					// If there is no search param, but the viewed month is the current month we don't need to set a search param
					if (!viewMonth && midMonth === new Date(Date.now()).toISOString().slice(0, 7)) {
						return;
					}
					
					
					// Set search params
					if (!viewMonth || viewMonth !== midMonth) {
						setSearchParams({ month: midMonth }, { replace: true, state: { disableScrollToTop: true } });
					}
				}}
				eventClick={(info) => {
					info.jsEvent.preventDefault();
					info.jsEvent.stopPropagation();
					
					if (info.event.url) {
						setEventBeingLoaded(info.event.url);
						navigate(info.event.url, { state: { disableScrollToTop: true } });
					}
				}}
				eventContent={(info) => {
					// Get the year this event belongs to
					const year = info.event.start?.getFullYear();
					
					
					// Render loading UI if needed
					if (year && !events?.[year]) {
						return <Skeleton style={{ borderRadius: '3px', lineHeight: 'inherit' }} />;
					}
					
					
					// Render the event
					return (
						<React.Fragment>
							{info.timeText && <div className='fc-event-time'>{info.timeText}</div>}
							<div className='fc-event-title fc-sticky'>
								<span>{info.event.title}</span>
								{info.event.url === eventBeingLoaded && <LoadingIcon icon={faRefresh} spin />}
							</div>
						</React.Fragment>
					);
				}}
				now={storybookNow ? new Date(Date.now()) : undefined}
			/>
			
			<EventDetailsModal pageID={pageID} onReady={handleEventDetailModal} pageTitle={pageTitle} />
		</CalendarContainer>
	);
};

export default Calendar;
